import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, MessageSquare, TrendingUp, LogOut, Mail, Phone, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({
    totalLearn: 0,
    totalHelp: 0,
    totalAll: 0
  });
  const [activeTab, setActiveTab] = useState('learn');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const isAuth = sessionStorage.getItem('adminAuth');
    if (!isAuth) {
      navigate('/admin/login');
      return;
    }

    fetchSubmissions();
  }, [navigate]);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/admin/submissions`);
      const data = response.data;
      
      setSubmissions(data);
      
      // Calculate stats
      const learnCount = data.filter(s => s.type === 'learn').length;
      const helpCount = data.filter(s => s.type === 'help').length;
      
      setStats({
        totalLearn: learnCount,
        totalHelp: helpCount,
        totalAll: data.length
      });
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Failed to load submissions');
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const filteredSubmissions = submissions.filter(s => s.type === activeTab);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600 text-sm">The 317 Foundation</p>
            </div>
            <div className="flex items-center gap-4">
              <a href="/" className="text-gray-600 hover:text-gray-900 text-sm font-medium">
                View Website
              </a>
              <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors">
                <LogOut size={18} />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="p-3 bg-cyan-100 rounded-lg">
                <Users className="text-cyan-600" size={24} />
              </div>
              <span className="text-3xl font-bold text-gray-900">{stats.totalAll}</span>
            </div>
            <p className="text-gray-600 font-medium">Total Submissions</p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="p-3 bg-blue-100 rounded-lg">
                <TrendingUp className="text-blue-600" size={24} />
              </div>
              <span className="text-3xl font-bold text-gray-900">{stats.totalLearn}</span>
            </div>
            <p className="text-gray-600 font-medium">Want to Learn</p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <div className="p-3 bg-purple-100 rounded-lg">
                <MessageSquare className="text-purple-600" size={24} />
              </div>
              <span className="text-3xl font-bold text-gray-900">{stats.totalHelp}</span>
            </div>
            <p className="text-gray-600 font-medium">Want to Help</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('learn')}
                className={`px-6 py-4 font-medium transition-colors ${activeTab === 'learn' ? 'border-b-2 border-cyan-500 text-cyan-600' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Student Applications ({stats.totalLearn})
              </button>
              <button
                onClick={() => setActiveTab('help')}
                className={`px-6 py-4 font-medium transition-colors ${activeTab === 'help' ? 'border-b-2 border-purple-500 text-purple-600' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Volunteer Applications ({stats.totalHelp})
              </button>
            </div>
          </div>

          {/* Submissions List */}
          <div className="p-6">
            {isLoading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
                <p className="text-gray-600 mt-4">Loading submissions...</p>
              </div>
            ) : filteredSubmissions.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="mx-auto text-gray-400 mb-4" size={48} />
                <p className="text-gray-600">No submissions yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredSubmissions.map((submission) => (
                  <div key={submission.id} className="bg-gray-50 rounded-lg p-6 border border-gray-200 hover:border-cyan-300 transition-colors">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-3">{submission.name}</h3>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-gray-700">
                            <Mail size={16} className="text-cyan-600" />
                            <a href={`mailto:${submission.email}`} className="hover:text-cyan-600">{submission.email}</a>
                          </div>
                          <div className="flex items-center gap-2 text-gray-700">
                            <Phone size={16} className="text-cyan-600" />
                            <a href={`tel:${submission.phone}`} className="hover:text-cyan-600">{submission.phone}</a>
                          </div>
                          <div className="flex items-center gap-2 text-gray-500 text-sm">
                            <Calendar size={16} />
                            <span>{formatDate(submission.timestamp)}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-2">
                          {activeTab === 'learn' ? 'Interest:' : 'Skills & Experience:'}
                        </p>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {submission.interest || submission.skills || 'No additional information provided'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;